/**
 * <kc-upsell-popup> — the product's upsells, offered between "add to cart" and the /cart page
 * (markup: snippets/kc-upsell-popup.liquid, opened by sections/kc-added-to-cart.liquid).
 *
 *   accepts(lines)  is one of the lines Shopify just added this popup's product?
 *   offer()         opens the popup; resolves with the /cart/add.js answer of the picked upsells,
 *                   or null when the shopper continues (or closes) without picking any.
 *
 * It never navigates: the caller does, so the tracking push and the redirect stay in one place.
 */
class KcUpsellPopup extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.dialog = this.querySelector("dialog");
    this.continueButton = this.querySelector("[data-popup-continue]");
    this.errorBox = this.querySelector("[data-popup-error]");
    this._resolve = null;

    this.querySelectorAll("[data-popup-offer]").forEach((card) => this._setupCard(card));

    this.addEventListener("click", (event) => {
      const value = event.target.closest("[data-popup-value]");
      if (value) return this._pickValue(value);

      const fabric = event.target.closest("[data-popup-fabric]");
      if (fabric) return this._pickFabric(fabric);

      const toggle = event.target.closest("[data-popup-toggle]");
      if (toggle) return this._toggle(toggle);

      if (event.target.closest("[data-popup-close]")) return this.dialog.close();
      if (event.target.closest("[data-popup-continue]")) return this._continue();

      // a click on the backdrop lands on the <dialog> itself
      if (event.target === this.dialog) this.dialog.close();
    });

    // X, Escape, backdrop: on to the cart without extras
    this.dialog.addEventListener("close", () => this._settle(null));

    // back from /cart through the back/forward cache: a fresh popup, not the one that was left open
    window.addEventListener("pageshow", (event) => {
      if (!event.persisted) return;
      this._busy(false);
      if (this.dialog.open) this.dialog.close();
    });
  }

  accepts(lines) {
    const productId = Number(this.dataset.productId);
    return (
      !!this.dialog &&
      typeof this.dialog.showModal === "function" &&
      (lines || []).some((line) => Number(line.product_id) === productId)
    );
  }

  offer() {
    return new Promise((resolve) => {
      this._resolve = resolve;
      this._showError("");
      this._busy(false);
      try {
        this.dialog.showModal();
      } catch (error) {
        this._settle(null);
      }
    });
  }

  _settle(value) {
    const resolve = this._resolve;
    this._resolve = null;
    if (resolve) resolve(value);
  }

  // ---- variants -----------------------------------------------------------------------------------------------------

  _setupCard(card) {
    let variants = [];
    try {
      variants = JSON.parse(card.querySelector("[data-popup-variants]")?.textContent || "[]");
    } catch (error) {
      variants = [];
    }
    card._variants = variants;
    if (!variants.length) return;

    const current = variants.find((variant) => String(variant.id) === card.dataset.variantId);
    if (current) this._apply(card, current, true);
  }

  _pickValue(button) {
    const card = button.closest("[data-popup-offer]");
    const group = button.closest("[data-popup-option]");
    if (!card || !group || !card._variants.length) return;

    const index = Number(group.dataset.popupOption);
    const current = card._variants.find((variant) => String(variant.id) === card.dataset.variantId);
    const wanted = current ? [...current.o] : [];
    wanted[index] = button.dataset.popupValue;

    // the exact combination when it can be bought, else the first purchasable variant with the picked value
    const variant =
      card._variants.find((candidate) => candidate.a && candidate.o.every((value, i) => value === wanted[i])) ||
      card._variants.find((candidate) => candidate.a && candidate.o[index] === wanted[index]);
    if (variant) this._apply(card, variant);
  }

  // a fabric pill: its colours replace the others, and the first one that can be bought is picked
  _pickFabric(button) {
    const group = button.closest("[data-popup-option]");
    if (!group) return;
    const first = [...group.querySelectorAll("[data-popup-value]")].find(
      (value) => value.dataset.group === button.dataset.popupFabric && !value.disabled
    );
    if (first) this._pickValue(first);
  }

  // everything on the card that depends on the chosen variant
  _apply(card, variant, initial = false) {
    const variants = card._variants;
    card.dataset.variantId = String(variant.id);

    card.querySelectorAll("[data-popup-option]").forEach((group) => {
      const index = Number(group.dataset.popupOption);
      // variants that share the options chosen BEFORE this one (colour depends on material, size on colour…)
      const siblings = variants.filter((candidate) => candidate.o.slice(0, index).every((value, i) => value === variant.o[i]));
      const selected = [...group.querySelectorAll("[data-popup-value]")].find(
        (button) => button.dataset.popupValue === variant.o[index]
      );
      const fabric = selected?.dataset.group;

      group.querySelectorAll("[data-popup-fabric]").forEach((pill) => {
        pill.setAttribute("aria-pressed", String(pill.dataset.popupFabric === fabric));
      });

      group.querySelectorAll("[data-popup-value]").forEach((button) => {
        const value = button.dataset.popupValue;
        const matches = siblings.filter((candidate) => candidate.o[index] === value);
        const otherFabric = fabric !== undefined && !!button.dataset.group && button.dataset.group !== fabric;
        button.hidden = otherFabric || !matches.length;
        button.disabled = !variants.some((candidate) => candidate.a && candidate.o[index] === value);
        button.setAttribute("aria-pressed", String(button === selected));

        // configurator-v2: the swatch of this colour in the chosen material
        const swatch = matches.find((candidate) => candidate.s)?.s;
        const dot = button.querySelector(".kc-upsell-popup__swatch");
        if (swatch && dot) dot.style.backgroundImage = `url("${swatch}")`;
      });

      const label = group.querySelector("[data-popup-option-value]");
      if (label) label.textContent = selected?.dataset.label || variant.o[index];
    });

    const price = card.querySelector("[data-popup-price]");
    if (price) price.textContent = variant.p;

    const compare = card.querySelector("[data-popup-compare]");
    if (compare) {
      compare.textContent = variant.c;
      compare.hidden = !variant.c;
    }

    const badge = card.querySelector("[data-popup-badge]");
    if (badge) {
      badge.textContent = `\u2212${variant.d}%`;
      badge.hidden = !(variant.d > 0);
    }

    const image = card.querySelector(".kc-upsell-popup__image");
    // the server already rendered the first variant's picture (with its srcset): only swap on a real change
    if (image && variant.i && !initial && image.dataset.shown !== variant.i) {
      image.dataset.shown = variant.i;
      image.removeAttribute("srcset");
      image.src = variant.i;
    }
  }

  // ---- picking ------------------------------------------------------------------------------------------------------

  _toggle(button) {
    const selected = button.getAttribute("aria-pressed") !== "true";
    button.setAttribute("aria-pressed", String(selected));
    button.closest("[data-popup-offer]")?.classList.toggle("is-selected", selected);
    this._showError("");
    this._updateContinueLabel();
  }

  _selectedCards() {
    return [...this.querySelectorAll("[data-popup-offer].is-selected")];
  }

  _updateContinueLabel() {
    this.continueButton.textContent = this._selectedCards().length
      ? this.dataset.labelContinueWith
      : this.dataset.labelContinue;
  }

  async _continue() {
    if (this._working) return;

    const items = this._selectedCards()
      .map((card) => ({ id: Number(card.dataset.variantId), quantity: 1 }))
      .filter((item) => item.id);

    if (!items.length) {
      this._busy(true);
      this._settle(null);
      return;
    }

    this._busy(true);
    this._showError("");
    try {
      const root = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest", Accept: "application/json" },
        body: JSON.stringify({ items }),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.description || json.message || "");
      // stays busy: the caller navigates to the cart now
      this._settle(json);
    } catch (error) {
      this._busy(false);
      this._showError(error.message || this.dataset.labelError);
    }
  }

  _busy(state) {
    this._working = state;
    if (state) this.continueButton.setAttribute("aria-busy", "true");
    else this.continueButton.removeAttribute("aria-busy");
    this.continueButton.disabled = state;
  }

  _showError(message) {
    if (!this.errorBox) return;
    this.errorBox.textContent = message;
    this.errorBox.hidden = !message;
  }
}

if (!window.customElements.get("kc-upsell-popup")) {
  window.customElements.define("kc-upsell-popup", KcUpsellPopup);
}
